export const CURRENCIES = {
  EUR: { symbol: '€', name: 'Euro', format: 'symbol-first' },
  USD: { symbol: '$', name: 'US Dollar', format: 'symbol-first' },
  GBP: { symbol: '£', name: 'British Pound', format: 'symbol-first' },
  DKK: { symbol: 'kr', name: 'Danish Krone', format: 'symbol-last' },
  SEK: { symbol: 'kr', name: 'Swedish Krona', format: 'symbol-last' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', format: 'symbol-last' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', format: 'symbol-last' },
  JPY: { symbol: '¥', name: 'Japanese Yen', format: 'symbol-first' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', format: 'symbol-first' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', format: 'symbol-first' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', format: 'symbol-first' },
} as const;

/**
 * Exchange rates relative to DKK (Danish Krone)
 * Updated: January 2025
 *
 * Note: These are approximate rates for CPM benchmarks.
 * For financial transactions, use real-time rates from a currency API.
 */
export const EXCHANGE_RATES_FROM_DKK = {
  EUR: 0.134, // 1 DKK = 0.134 EUR (1 EUR = ~7.46 DKK)
  USD: 0.143, // 1 DKK = 0.143 USD (1 USD = ~7.00 DKK)
  GBP: 0.114, // 1 DKK = 0.114 GBP (1 GBP = ~8.77 DKK)
  DKK: 1.0, // Base currency
  SEK: 1.52, // 1 DKK = 1.52 SEK (1 SEK = ~0.66 DKK)
  NOK: 1.52, // 1 DKK = 1.52 NOK (1 NOK = ~0.66 DKK)
  CHF: 0.126, // 1 DKK = 0.126 CHF (1 CHF = ~7.94 DKK)
  JPY: 21.0, // 1 DKK = 21 JPY (1 JPY = ~0.048 DKK)
  CNY: 1.03, // 1 DKK = 1.03 CNY (1 CNY = ~0.97 DKK)
  AUD: 0.224, // 1 DKK = 0.224 AUD (1 AUD = ~4.46 DKK)
  CAD: 0.202, // 1 DKK = 0.202 CAD (1 CAD = ~4.95 DKK)
} as const;

export type CurrencyCode = keyof typeof CURRENCIES;

export function formatCurrency(amount: number, currencyCode: string = 'EUR'): string {
  const currency = CURRENCIES[currencyCode as CurrencyCode] || CURRENCIES.EUR;

  // Format number with thousand separators
  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  // Place symbol based on format
  if (currency.format === 'symbol-first') {
    return `${currency.symbol}${formattedNumber}`;
  } else {
    return `${formattedNumber} ${currency.symbol}`;
  }
}

export function getCurrencySymbol(currencyCode: string = 'EUR'): string {
  return CURRENCIES[currencyCode as CurrencyCode]?.symbol || '€';
}

export function formatCurrencyWithDecimals(
  amount: number,
  currencyCode: string = 'EUR',
  decimals: number = 2
): string {
  const currency = CURRENCIES[currencyCode as CurrencyCode] || CURRENCIES.EUR;

  // Format number with decimals
  const formattedNumber = amount.toFixed(decimals);

  // Place symbol based on format
  if (currency.format === 'symbol-first') {
    return `${currency.symbol}${formattedNumber}`;
  } else {
    return `${formattedNumber} ${currency.symbol}`;
  }
}

/**
 * Convert amount from DKK to target currency
 *
 * @param amountInDKK - Amount in Danish Kroner
 * @param targetCurrency - Target currency code (EUR, USD, GBP, etc.)
 * @returns Converted amount (rounded to 2 decimals)
 *
 * @example
 * convertFromDKK(100, 'EUR') // Returns 13.4 (100 DKK = 13.4 EUR)
 * convertFromDKK(159, 'USD') // Returns 22.7 (159 DKK = 22.7 USD)
 */
export function convertFromDKK(amountInDKK: number, targetCurrency: string = 'DKK'): number {
  const rate = EXCHANGE_RATES_FROM_DKK[targetCurrency as CurrencyCode];
  if (!rate) {
    // Unknown currency, return original amount
    return amountInDKK;
  }
  return Math.round(amountInDKK * rate * 100) / 100; // Round to 2 decimals
}

/**
 * Convert amount between any two currencies using live exchange rates
 *
 * @param amount - Amount in source currency
 * @param fromCurrency - Source currency code (EUR, USD, GBP, etc.)
 * @param toCurrency - Target currency code (EUR, USD, GBP, etc.)
 * @param exchangeRates - Live exchange rates from DKK (from useExchangeRates hook)
 * @returns Converted amount (rounded to 2 decimals)
 *
 * @example
 * // Convert $22.20 USD to EUR
 * const rates = { USD: 0.143, EUR: 0.134 }; // Rates from DKK
 * convertCurrency(22.20, 'USD', 'EUR', rates) // Returns ~20.93 EUR
 *
 * @example
 * // Convert €9.50 EUR to DKK
 * const rates = { EUR: 0.134 };
 * convertCurrency(9.50, 'EUR', 'DKK', rates) // Returns ~70.90 DKK
 */
export function convertCurrency(
  amount: number,
  fromCurrency: CurrencyCode | string,
  toCurrency: CurrencyCode | string,
  exchangeRates?: Record<string, number>
): number {
  // Same currency, no conversion needed
  if (fromCurrency === toCurrency) {
    return amount;
  }

  // Use live rates if provided, otherwise fall back to hardcoded rates
  const rates = exchangeRates || EXCHANGE_RATES_FROM_DKK;

  const fromRate = rates[fromCurrency as CurrencyCode];
  const toRate = rates[toCurrency as CurrencyCode];

  // If rates not available, return original amount
  if (!fromRate || !toRate) {
    return amount;
  }

  // Convert: Source → DKK → Target
  // Example: USD → DKK → EUR
  // 1. USD to DKK: amount / fromRate (e.g., $22.20 / 0.143 = 155.24 DKK)
  // 2. DKK to EUR: result * toRate (e.g., 155.24 × 0.134 = 20.80 EUR)
  const amountInDKK = amount / fromRate;
  const convertedAmount = amountInDKK * toRate;

  return Math.round(convertedAmount * 100) / 100; // Round to 2 decimals
}
